import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store';
import LoadingSpinner from '../components/common/LoadingSpinner';
import type { Favorite, Repository } from '../types';

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    favorites,
    favoritesLoading,
    loadFavorites,
    toggleFavorite
  } = useAppStore();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    // 延迟加载收藏列表，确保认证状态已经稳定
    const timer = setTimeout(() => {
      loadFavorites();
    }, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated, navigate, loadFavorites]);

  const handleRemoveFavorite = async (project: Repository) => {
    await toggleFavorite(project);
  };

  // 格式化 stars 数量为 k 单位
  const formatStars = (stars: number): string => {
    if (stars >= 1000) {
      return (Math.round(stars / 100) / 10) + 'k';
    }
    return stars.toString();
  };

  if (!isAuthenticated) {
    return null;
  }

  const favoriteCount = favorites.length;
  const totalStars = favorites.reduce(
    (sum, fav: Favorite) => sum + (fav.project_stars || 0),
    0
  );

  if (favoritesLoading) {
    return (
      <div className="favorites-page">
        <div className="favorites-page-header">
          <h2 className="favorites-page-title">
            <i className="fas fa-heart text-danger me-2"></i>
            我的收藏
          </h2>
        </div>
        <div className="favorites-page-content">
          <LoadingSpinner size="lg" text="加载收藏列表..." />
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <div className="favorites-page-header">
        <h2 className="favorites-page-title">
          <i className="fas fa-heart text-danger me-2"></i>
          我的收藏
        </h2>
        {favoriteCount > 0 && (
          <div className="favorites-summary">
            <div className="favorites-summary-item">
              <span className="favorites-summary-label">收藏项目</span>
              <span className="favorites-summary-value">{favoriteCount}</span>
            </div>
            <div className="favorites-summary-item">
              <span className="favorites-summary-label">总 Star 数</span>
              <span className="favorites-summary-value">
                {formatStars(totalStars)}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="favorites-page-content">
        {favorites.length === 0 ? (
          <div className="favorites-empty">
            <i
              className="fas fa-heart-broken text-muted mb-3"
              style={{ fontSize: '3rem' }}
            ></i>
            <h4 className="text-muted">暂无收藏项目</h4>
            <p className="text-muted">
              去首页搜索并收藏一些有趣的项目吧！
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              开始搜索
            </button>
          </div>
        ) : (
          <div className="favorites-grid">
            {favorites.map((favorite: Favorite) => (
              <div key={favorite.id} className="favorite-grid-item">
                <div className="favorite-project-card">
                  <div className="favorite-card-body">
                    <div className="favorite-header">
                      <div className="favorite-title">
                        <a
                          href={favorite.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="favorite-project-link"
                        >
                          <i className="fab fa-github me-2"></i>
                          {favorite.project_name}
                        </a>
                      </div>
                    </div>

                    <p className="favorite-description">
                      {favorite.project_description || '暂无描述'}
                    </p>

                    <div className="favorite-footer">
                      <div className="favorite-stats">
                        {favorite.project_language && (
                          <span className="favorite-stat-item language">
                            <i className="fas fa-code"></i>
                            {favorite.project_language}
                          </span>
                        )}
                        <span className="favorite-stat-item stars">
                          <i className="fas fa-star"></i>
                          {formatStars(favorite.project_stars)}
                        </span>
                      </div>
                      <div className="favorite-date">
                        <span className="favorite-date-text">收藏时间：</span>
                        <span className="favorite-date-value">
                          {new Date(favorite.created_at).toLocaleDateString()}
                        </span>
                        <button
                          className="favorite-remove-btn ms-2"
                          onClick={() =>
                            handleRemoveFavorite({
                              name: favorite.project_name,
                              full_name: favorite.project_full_name,
                              url: favorite.project_url,
                              description:
                                favorite.project_description || '',
                              stars: favorite.project_stars,
                              forks: 0,
                              owner:
                                favorite.project_full_name.split('/')[0] || '',
                              owner_avatar: '',
                              created_at: favorite.created_at,
                              updated_at: favorite.created_at,
                              language: favorite.project_language,
                              topics: []
                            })
                          }
                          title="取消收藏"
                        >
                          取消收藏
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;

